import { useEffect, useState, type FormEvent } from 'react'
import { Icon } from '@iconify/react'
import type { RaspadinhaTimeSlotWinner } from '@/types/raspadinha-campaign'
import layoutStyles from '@/layouts/CampanhasLayout/CampanhasLayout.module.css'
import editStyles from '../CampanhasPage/CampanhaEditPage.module.css'

export type WinnerFormValues = {
  name: string
  document: string
  prize_label: string
  notes: string
}

const EMPTY_WINNER_FORM: WinnerFormValues = {
  name: '',
  document: '',
  prize_label: '',
  notes: '',
}

type WinnerFormModalProps = {
  isOpen: boolean
  mode: 'create' | 'edit'
  initialWinner: RaspadinhaTimeSlotWinner | null
  onClose: () => void
  onSave: (values: WinnerFormValues) => void
}

export function WinnerFormModal({
  isOpen,
  mode,
  initialWinner,
  onClose,
  onSave,
}: WinnerFormModalProps) {
  const [form, setForm] = useState<WinnerFormValues>(EMPTY_WINNER_FORM)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)

  useEffect(() => {
    if (!isOpen) return

    setErrorMessage(null)
    setForm(
      initialWinner
        ? {
            name: initialWinner.name,
            document: initialWinner.document,
            prize_label: initialWinner.prize_label,
            notes: initialWinner.notes,
          }
        : EMPTY_WINNER_FORM
    )
  }, [initialWinner, isOpen])

  if (!isOpen) return null

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()

    if (!form.name.trim()) {
      setErrorMessage('Informe o nome do ganhador.')
      return
    }

    onSave({
      name: form.name.trim(),
      document: form.document.trim(),
      prize_label: form.prize_label.trim(),
      notes: form.notes.trim(),
    })
  }

  return (
    <div
      className={layoutStyles.modalOverlay}
      role="dialog"
      aria-modal="true"
      aria-labelledby="winner-form-modal-title"
      onClick={(event) => {
        if (event.target === event.currentTarget) {
          onClose()
        }
      }}
    >
      <div className={layoutStyles.modalCard}>
        <div className={layoutStyles.modalHeader}>
          <div>
            <h2 id="winner-form-modal-title" className={layoutStyles.modalTitle}>
              {mode === 'create' ? 'Adicionar ganhador' : 'Editar ganhador'}
            </h2>
            <p className={layoutStyles.modalSubtitle}>
              Preencha os dados do ganhador vinculado a este horário de contemplação.
            </p>
          </div>
          <button
            type="button"
            className={layoutStyles.modalClose}
            onClick={onClose}
            aria-label="Fechar formulário de ganhador"
          >
            <Icon icon="mdi:close" aria-hidden />
          </button>
        </div>

        <form className={layoutStyles.modalForm} onSubmit={handleSubmit}>
          <div className={layoutStyles.modalBody}>
            <div className={editStyles.editGrid2}>
              <div className={editStyles.editField}>
                <label className={editStyles.editLabel} htmlFor="winner-form-name">
                  Nome *
                </label>
                <input
                  id="winner-form-name"
                  type="text"
                  className={editStyles.editInput}
                  value={form.name}
                  onChange={(event) => {
                    setErrorMessage(null)
                    setForm((previous) => ({ ...previous, name: event.target.value }))
                  }}
                  autoFocus
                  required
                />
              </div>
              <div className={editStyles.editField}>
                <label className={editStyles.editLabel} htmlFor="winner-form-document">
                  CPF / documento
                </label>
                <input
                  id="winner-form-document"
                  type="text"
                  className={editStyles.editInput}
                  value={form.document}
                  onChange={(event) =>
                    setForm((previous) => ({ ...previous, document: event.target.value }))
                  }
                  placeholder="000.000.000-00"
                />
              </div>
            </div>

            <div className={editStyles.editField}>
              <label className={editStyles.editLabel} htmlFor="winner-form-prize">
                Prêmio
              </label>
              <input
                id="winner-form-prize"
                type="text"
                className={editStyles.editInput}
                value={form.prize_label}
                onChange={(event) =>
                  setForm((previous) => ({ ...previous, prize_label: event.target.value }))
                }
                placeholder="Ex: PIX R$ 100"
              />
            </div>

            <div className={editStyles.editField}>
              <label className={editStyles.editLabel} htmlFor="winner-form-notes">
                Observações
              </label>
              <input
                id="winner-form-notes"
                type="text"
                className={editStyles.editInput}
                value={form.notes}
                onChange={(event) =>
                  setForm((previous) => ({ ...previous, notes: event.target.value }))
                }
                placeholder="Informações adicionais (opcional)"
              />
            </div>

            {errorMessage ? (
              <div className={layoutStyles.modalError} role="alert">
                {errorMessage}
              </div>
            ) : null}
          </div>

          <div className={layoutStyles.modalFooter}>
            <button
              type="button"
              className={layoutStyles.modalSecondaryButton}
              onClick={onClose}
            >
              Cancelar
            </button>
            <button type="submit" className={layoutStyles.modalPrimaryButton}>
              {mode === 'create' ? 'Adicionar ganhador' : 'Salvar alterações'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
